import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const AdminNotesValidation = () => {
  const [pendingGrades, setPendingGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingGrades();
  }, []);

  const fetchPendingGrades = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/grades/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingGrades(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/admin/grades/${id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingGrades((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la validation');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter cette note ? Elle sera supprimée et le prof devra la ressaisir.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/admin/grades/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingGrades((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors du rejet');
    }
  };

  const handleValidateAll = async () => {
    if (!window.confirm(`Valider TOUTES les ${pendingGrades.length} notes ?`)) return;
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/grades/validate-all-pending', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${res.data.count || 0} notes validées`);
      fetchPendingGrades();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const classes = useMemo(() => {
    const set = new Set(pendingGrades.map((g) => g.class_name).filter(Boolean));
    return Array.from(set).sort();
  }, [pendingGrades]);

  const subjects = useMemo(() => {
    const set = new Set(pendingGrades.map((g) => g.subject).filter(Boolean));
    return Array.from(set).sort();
  }, [pendingGrades]);

  const filteredGrades = useMemo(() => {
    return pendingGrades.filter((g) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        if (
          !(g.student_name || '').toLowerCase().includes(t) &&
          !(g.teacher_name || '').toLowerCase().includes(t) &&
          !(g.evaluation_title || '').toLowerCase().includes(t)
        ) return false;
      }
      if (filterClass !== 'all' && g.class_name !== filterClass) return false;
      if (filterSubject !== 'all' && g.subject !== filterSubject) return false;
      return true;
    });
  }, [pendingGrades, searchTerm, filterClass, filterSubject]);

  const getScoreColor = (score, max) => {
    const ratio = (parseFloat(score) / parseFloat(max)) * 100;
    if (ratio >= 80) return 'text-green-600';
    if (ratio >= 60) return 'text-blue-600';
    if (ratio >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📝 Validation des notes</h1>
          <p className="text-gray-500">Vérifiez et validez les notes saisies par les professeurs</p>
        </div>
        {pendingGrades.length > 0 && (
          <button
            onClick={handleValidateAll}
            disabled={processing}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50"
          >
            ✅ Tout valider ({pendingGrades.length})
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Une fois validées, les notes sont <strong>publiées</strong> et
          visibles par les élèves et les parents. Le professeur ne pourra plus les modifier.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 mb-1">⏳ En attente</p>
          <p className="text-2xl font-bold text-orange-600">{pendingGrades.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">🏫 Classes</p>
          <p className="text-2xl font-bold text-blue-600">{classes.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
          <p className="text-xs text-gray-500 mb-1">📚 Matières</p>
          <p className="text-2xl font-bold text-purple-600">{subjects.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">📊 Affichées</p>
          <p className="text-2xl font-bold text-green-600">{filteredGrades.length}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher (élève, prof, évaluation)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les classes</option>
            {classes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les matières</option>
            {subjects.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="text-sm text-gray-500 flex items-center px-2">
            {filteredGrades.length} note(s)
          </div>
        </div>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : filteredGrades.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">🎉</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucune note en attente</p>
          <p className="text-sm text-gray-500">
            {pendingGrades.length === 0
              ? 'Toutes les notes ont été validées. Bon travail !'
              : 'Aucun résultat pour ces filtres.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Classe</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matière</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Évaluation</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Prof</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Note</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Appréciation</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGrades.map((g) => (
                  <tr key={g.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{g.student_name}</td>
                    <td className="py-3 px-4 text-sm">{g.class_name}</td>
                    <td className="py-3 px-4 text-sm">{g.subject}</td>
                    <td className="py-3 px-4 text-sm">{g.evaluation_title}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{g.teacher_name}</td>
                    <td className={`py-3 px-4 font-bold ${getScoreColor(g.score, g.max_score)}`}>
                      {g.score}/{g.max_score}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {g.appreciation || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleValidate(g.id)}
                          className="text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100 transition font-medium"
                          title="Valider"
                        >
                          ✅
                        </button>
                        <button
                          onClick={() => handleReject(g.id)}
                          className="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 transition font-medium"
                          title="Rejeter"
                        >
                          ❌
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotesValidation;